import { useRef } from 'react';
import { isEqual } from 'lodash';
import { IMaterialTableOptions, IMaterialTableProps } from '../../components/Table';
import { ITableDimensions } from '../useTableDimensions';
import { usePrevious } from '../index';
import { getFloatWithPrecision } from '../../services/helpers';
import { fillVirtualizedTableColumnWidth, parseVirtualizedTableColumnWidth } from './helpers';
import WidthTypeEnum from '../../enums/VirtualizedTableSupportedColumnWidthTypeEnum';

export interface IUseVirtualizedTableColumnWidths<RowData extends object> {
  columns: IMaterialTableProps<RowData>['columns'];
  tableWidth: ITableDimensions['tableWidth'];
  disableVirtualization: IMaterialTableProps<RowData>['disableVirtualization'];
  selection: IMaterialTableOptions<RowData>['selection'];
  selectionColumnWidth: number;
}

/**
 * This hook will properly set widths on:
 * * table width change
 * * update(merge) columns on columns change (for example, sorting change)
*/
const useVirtualizedTableColumnWidths = <RowData extends object>(
  props: IUseVirtualizedTableColumnWidths<RowData>,
): IMaterialTableProps<RowData>['columns'] => {
  const {
    columns: originalColumns,
    tableWidth,
    disableVirtualization,
    selection,
    selectionColumnWidth,
  } = props;

  // store the previous values to detect changes
  const prevTableWidth = usePrevious(tableWidth);
  const prevSelection = usePrevious(selection);
  const prevSelectionColumnWidth = usePrevious(selectionColumnWidth);

  // store original columns to detect column changes
  const cachedOriginalColumns = useRef<IMaterialTableProps<RowData>['columns']>(originalColumns);

  // store processed columns to return proper columns when neither width nor columns have changed
  const processedColumns = useRef<IMaterialTableProps<RowData>['columns']>();

  if (disableVirtualization || !tableWidth) {
    // return original columns if virtualization is disabled or width hasn't been calculated yet
    return originalColumns;
  }

  /*
    initial render is handled by checking for '!tableWidth', so no further checking for 'undefined'
    is needed for 'prevTableWidth', 'prevSelection', and 'prevSelectionColumnWidth'
  */
  const hasWidthChanged = prevTableWidth !== tableWidth;
  const hasSelectionChanged = prevSelection !== selection;
  const hasSelectionColumnWidthChanged = prevSelectionColumnWidth !== selectionColumnWidth;

  if (
    !!processedColumns.current
    && !hasWidthChanged
    && !hasSelectionChanged
    && !hasSelectionColumnWidthChanged
  ) {
    if (isEqual(cachedOriginalColumns.current, originalColumns)) {
      return processedColumns.current; // nothing changed, skip calculations
    }
    cachedOriginalColumns.current = originalColumns;
    // only columns changed - merge columns
    processedColumns.current = originalColumns.map(
      (column, index) => {
        const prevColumn = processedColumns.current![index];
        return {
          ...column,
          width: prevColumn.width,
          // @ts-ignore
          tableData: { ...column.tableData, width: prevColumn.tableData.width },
        };
      },
    );
    return processedColumns.current;
  }

  // recalculate width for each column

  let reservedFixedSpace = 0; // space reserved by columns with width in PX
  let totalRegisteredPercents = 0; // used to check for the correct amount of total percent

  const columnsIdxWithoutWidth: number[] = [];
  const columnIdxToProcessedPercentMap = new Map<number, number>();

  processedColumns.current = new Array(originalColumns.length);

  /*
   in this forEach, register fields without specified width, fill columns where the width was set
   in PX, and register fields with width in percent
  */
  originalColumns.forEach((column, idx) => {
    const { width: initialWidth } = column;

    if (typeof initialWidth === 'undefined') {
      columnsIdxWithoutWidth.push(idx);
      return;
    }

    const { width: processedWidth, valueType } = parseVirtualizedTableColumnWidth(initialWidth);

    if (valueType === WidthTypeEnum.PX) {
      processedColumns.current![idx] = fillVirtualizedTableColumnWidth<RowData>(
        column,
        processedWidth,
      );
      reservedFixedSpace = getFloatWithPrecision(reservedFixedSpace + processedWidth);
    }

    if (valueType === WidthTypeEnum.PERCENT) {
      columnIdxToProcessedPercentMap.set(idx, processedWidth);
      totalRegisteredPercents = getFloatWithPrecision(
        totalRegisteredPercents + processedWidth,
      );
    }
  });

  if (process.env.NODE_ENV !== 'production') {
    if (columnsIdxWithoutWidth.length + columnIdxToProcessedPercentMap.size === 0) {
      // eslint-disable-next-line no-console
      console.warn(
        'The virtualized table uses "tableLayout: \'auto\'", which may shrink/expand columns'
        + ` even if they have a fixed width in '${
          WidthTypeEnum.PX
        }' thus table may not behave as you`
        + ' expect it to. Consider omitting width for some columns or setting its value to'
        + ` '${WidthTypeEnum.PERCENT}'.`,
      );
    }

    if (
      !columnsIdxWithoutWidth.length
      && !!columnIdxToProcessedPercentMap.size
      && (totalRegisteredPercents < 99 || totalRegisteredPercents > 101)
    ) {
      /*
       we cannot do a comparison like 'totalRegisteredPercents === 100' since we work with floats;

       cases like, for example, a table with 6 columns where all widths are equal
       (each will have 100 / 6) will not add up and be equal to 100, there will be a margin error
       and the actual value will be 100.02 with current precision;
       and vice versa - a table with 3 columns (100 / 3) will end up with 99.99;

       in both cases, having an acceptable margin of 1% to both sides doesn't make much visual
       difference since virtualized table uses "tableLayout: 'auto'" which properly distributes the
       space
      */
      throw new Error(
        'The sum of provided percent is not near 100% ± 1%, while there are no columns without'
        + ' width. The virtualized table may not behave as you expect. Consider omitting some of'
        + ' the percent values so the table will fill the available space by itself, or sum up the'
        + ' values so they are close to 100% ± 1%.',
      );
    }
  }

  let unusedSpace = Math.max(getFloatWithPrecision(
    tableWidth - reservedFixedSpace - (selection ? selectionColumnWidth : 0),
  ), 0);

  if (columnIdxToProcessedPercentMap.size) {
    // handle columns whose width was specified in percent
    let usedWidth = 0;
    columnIdxToProcessedPercentMap.forEach((widthInPercent, index) => {
      const widthInPx = getFloatWithPrecision((unusedSpace * widthInPercent) / 100);
      usedWidth = getFloatWithPrecision(usedWidth + widthInPx);
      processedColumns.current![index] = fillVirtualizedTableColumnWidth<RowData>(
        originalColumns[index],
        widthInPx,
      );
    });
    unusedSpace = getFloatWithPrecision(unusedSpace - usedWidth);
  }

  if (columnsIdxWithoutWidth.length) {
    // equally distribute unused space among columns without specified width
    const unusedSpacePerColumn = getFloatWithPrecision(unusedSpace / columnsIdxWithoutWidth.length);
    columnsIdxWithoutWidth.forEach((index) => {
      processedColumns.current![index] = fillVirtualizedTableColumnWidth<RowData>(
        originalColumns[index],
        unusedSpacePerColumn,
      );
    });
  }

  return processedColumns.current;
};

export default useVirtualizedTableColumnWidths;
